import { useCallback, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import CloseIcon from "@mui/icons-material/Close";
import { getAuth } from "firebase/auth";
import {
  doc,
  getFirestore,
  onSnapshot,
  serverTimestamp,
  setDoc,
  type Timestamp,
} from "firebase/firestore";
import { JarvisTheme } from "../../theme/jarvisTheme";

export type MemorySectionId = "identity" | "lessons" | "active_project";

const MEMORY_SECTIONS: Array<{
  id: MemorySectionId;
  title: string;
  hint: string;
}> = [
  {
    id: "identity",
    title: "About you",
    hint: "Who you are, preferences, standing context",
  },
  {
    id: "lessons",
    title: "What I've learned",
    hint: "Durable facts Jarvis should remember",
  },
  {
    id: "active_project",
    title: "Current focus",
    hint: "Structured focus / mode (also shown as Focus chip)",
  },
];

type MemoryUpdatedBy = "ai" | "user";

type MemorySectionState = {
  content: string;
  draft: string;
  updatedAt: Timestamp | null;
  updatedBy: MemoryUpdatedBy | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  dirty: boolean;
};

function emptySection(): MemorySectionState {
  return {
    content: "",
    draft: "",
    updatedAt: null,
    updatedBy: null,
    loading: true,
    saving: false,
    error: null,
    dirty: false,
  };
}

function formatUpdatedAt(ts: Timestamp | null): string {
  if (!ts || typeof ts.toDate !== "function") return "—";
  return ts.toDate().toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

type MemoryDrawerProps = {
  open: boolean;
  onClose: () => void;
  scrollToSection?: MemorySectionId | null;
};

export function MemoryDrawer({
  open,
  onClose,
  scrollToSection,
}: MemoryDrawerProps) {
  const uid = getAuth().currentUser?.uid ?? null;
  const sectionRefs = useRef<Partial<Record<MemorySectionId, HTMLDivElement>>>(
    {},
  );
  const [sections, setSections] = useState<
    Record<MemorySectionId, MemorySectionState>
  >({
    identity: emptySection(),
    lessons: emptySection(),
    active_project: emptySection(),
  });

  useEffect(() => {
    if (!uid) return;

    const unsubs = MEMORY_SECTIONS.map(({ id }) => {
      const ref = doc(getFirestore(), "jarvis_memory", uid, id, "data");
      return onSnapshot(
        ref,
        (snap) => {
          const data = snap.data();
          const content =
            typeof data?.content === "string" ? data.content : "";
          const updatedBy =
            data?.updatedBy === "user" || data?.updatedBy === "ai"
              ? data.updatedBy
              : null;
          setSections((prev) => {
            const section = prev[id];
            const keepLocalDraft = section.dirty || section.saving;
            return {
              ...prev,
              [id]: {
                ...section,
                content,
                draft: keepLocalDraft ? section.draft : content,
                dirty: keepLocalDraft ? section.draft !== content : false,
                updatedAt: (data?.updatedAt as Timestamp | undefined) ?? null,
                updatedBy,
                loading: false,
                error: null,
              },
            };
          });
        },
        (err) => {
          setSections((prev) => ({
            ...prev,
            [id]: {
              ...prev[id],
              loading: false,
              error: err.message || "Failed to load.",
            },
          }));
        },
      );
    });

    return () => unsubs.forEach((u) => u());
  }, [uid]);

  useEffect(() => {
    if (!open || !scrollToSection) return;
    const el = sectionRefs.current[scrollToSection];
    if (!el) return;
    const timer = window.setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [open, scrollToSection]);

  const setDraft = useCallback((id: MemorySectionId, draft: string) => {
    setSections((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        draft,
        dirty: draft !== prev[id].content,
      },
    }));
  }, []);

  const saveSection = useCallback(
    async (id: MemorySectionId) => {
      if (!uid) return;
      const section = sections[id];
      setSections((prev) => ({
        ...prev,
        [id]: { ...prev[id], saving: true, error: null },
      }));

      try {
        const ref = doc(getFirestore(), "jarvis_memory", uid, id, "data");
        await setDoc(
          ref,
          {
            content: section.draft,
            updatedAt: serverTimestamp(),
            updatedBy: "user",
          },
          { merge: true },
        );
        setSections((prev) => ({
          ...prev,
          [id]: {
            ...prev[id],
            saving: false,
            dirty: false,
          },
        }));
      } catch (e: unknown) {
        setSections((prev) => ({
          ...prev,
          [id]: {
            ...prev[id],
            saving: false,
            error: e instanceof Error ? e.message : "Save failed.",
          },
        }));
      }
    },
    [uid, sections],
  );

  const anyLoading = MEMORY_SECTIONS.some(({ id }) => sections[id].loading);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 420 },
          maxWidth: "100%",
          bgcolor: JarvisTheme.colors.bg1,
          backgroundImage: "none",
          borderLeft: `1px solid ${JarvisTheme.colors.panelBorder}`,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${JarvisTheme.colors.panelBorder}`,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: JarvisTheme.fonts.ui,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                fontSize: "0.75rem",
                color: JarvisTheme.colors.accent,
              }}
            >
              Memory
            </Typography>
            <Typography
              sx={{
                fontSize: "0.8rem",
                color: JarvisTheme.colors.muted,
                opacity: 0.85,
              }}
            >
              What Jarvis knows about you
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            aria-label="Close memory drawer"
            sx={{ color: JarvisTheme.colors.muted }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 2 }}>
          {!uid && (
            <Alert severity="warning">Sign in to view memory.</Alert>
          )}

          {uid && anyLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress
                size={22}
                sx={{ color: JarvisTheme.colors.accent }}
              />
            </Box>
          )}

          {uid &&
            MEMORY_SECTIONS.map(({ id, title, hint }) => {
              const section = sections[id];
              if (section.loading) return null;

              return (
                <Box
                  key={id}
                  ref={(el: HTMLDivElement | null) => {
                    if (el) sectionRefs.current[id] = el;
                  }}
                  sx={{ mb: 3 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 0.75,
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: JarvisTheme.fonts.ui,
                        fontSize: "0.85rem",
                        fontWeight: 600,
                        color: JarvisTheme.colors.text,
                      }}
                    >
                      {title}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                      {section.updatedBy && (
                        <Chip
                          label={section.updatedBy === "user" ? "You" : "AI"}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.62rem",
                            fontFamily: JarvisTheme.fonts.mono,
                            bgcolor: JarvisTheme.colors.panelSoft,
                            color: JarvisTheme.colors.muted,
                            border: `1px solid ${JarvisTheme.colors.panelBorder}`,
                          }}
                        />
                      )}
                      <Typography
                        sx={{
                          fontSize: "0.68rem",
                          color: JarvisTheme.colors.muted,
                          fontFamily: JarvisTheme.fonts.mono,
                        }}
                      >
                        {formatUpdatedAt(section.updatedAt)}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography
                    sx={{
                      fontSize: "0.72rem",
                      color: JarvisTheme.colors.muted,
                      mb: 1,
                      opacity: 0.8,
                    }}
                  >
                    {hint}
                  </Typography>

                  {section.error && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                      {section.error}
                    </Alert>
                  )}

                  <TextField
                    multiline
                    minRows={id === "active_project" ? 6 : 4}
                    maxRows={16}
                    fullWidth
                    disabled={section.saving}
                    value={section.draft}
                    onChange={(e) => setDraft(id, e.target.value)}
                    placeholder={
                      section.content.trim()
                        ? undefined
                        : "(empty — Jarvis will learn over time)"
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        fontFamily: JarvisTheme.fonts.ui,
                        fontSize: "0.85rem",
                        color: JarvisTheme.colors.text,
                        bgcolor: JarvisTheme.colors.panelSoft,
                        "& fieldset": {
                          borderColor: JarvisTheme.colors.panelBorder,
                        },
                        "&:hover fieldset": {
                          borderColor: JarvisTheme.colors.accentBorder,
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: JarvisTheme.colors.accent,
                        },
                      },
                    }}
                  />

                  <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={!section.dirty || section.saving}
                      onClick={() => void saveSection(id)}
                      sx={{
                        fontFamily: JarvisTheme.fonts.ui,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        fontSize: "0.7rem",
                        bgcolor: JarvisTheme.colors.accent,
                        color: JarvisTheme.colors.bg0,
                        "&:hover": {
                          bgcolor: "rgba(88, 230, 255, 0.85)",
                        },
                        "&.Mui-disabled": {
                          bgcolor: JarvisTheme.colors.accentTrack,
                          color: JarvisTheme.colors.muted,
                        },
                      }}
                    >
                      {section.saving ? "Saving…" : "Save"}
                    </Button>
                  </Box>
                </Box>
              );
            })}
        </Box>
      </Box>
    </Drawer>
  );
}
